import {getButtonOutlineWhite} from '@hconnect/common/styles'
import {PageContainer, buttonOnBlueSx, mergeSxs} from '@hconnect/uikit/src/lib2'
import {Add, FileDownload} from '@mui/icons-material'
import {LoadingButton} from '@mui/lab'
import {Grid, Theme, useMediaQuery, Stack, Button, Box, Typography} from '@mui/material'
import {identity} from 'lodash'
import React, {useCallback, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {ACTION_TABLE_COLUMNS, ActionDialogWrapper, ActionsPaginatedTable} from '../components'
import {FEATURE_NAMESPACE} from '../consts'
import {ActionsContainer, ActionSideCardContainer, FilterBar} from '../containers'
import {
  useFilteredActions,
  usePlantActionsDownload,
  useCostLeadershipDownload,
  useActionFilters,
  usePlantUpmId,
  useTablePage,
  useTableRowsPerPage,
  useTableSortDir,
  useActionId,
  useSearchParameter
} from '../hooks'
import {ActionItem, ActionItemKey, PaginatedList, SearchParamGetSet} from '../types'
import {getFilteredData} from '../utils'

const stringToActionItemKey = (str?: string): ActionItemKey | undefined =>
  str && ACTION_TABLE_COLUMNS.includes(str as ActionItemKey) ? (str as ActionItemKey) : undefined

const useActionSearchParam = (): SearchParamGetSet<ActionItemKey> => {
  return useSearchParameter('tableSortKey', identity, stringToActionItemKey)
}

export const ActionsPage: React.FC<{inverted?: boolean}> = ({inverted = false}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)
  const [pageIndex, setPageIndex] = useTablePage()
  const [rowsPerPage, setRowsPerPage] = useTableRowsPerPage()
  const [sortKey, setSortKey] = useActionSearchParam()
  const [sortDir, setSortDir] = useTableSortDir()
  const [actionId, setActionId] = useActionId()
  const [createdAction, setCreatedAction] = useState<ActionItem>()
  const plantId = usePlantUpmId()
  useActionFilters(() => {
    // reset page index on filter change; writing it when already on the first page
    // would navigate from a not yet committed URL and drop the filter just applied
    if (pageIndex !== 0) {
      setPageIndex(0)
    }
  })

  const isSmall = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'))

  const {questionCategories} = useActionFilters()
  const {data: filteredActions} = useFilteredActions({
    page: pageIndex,
    rowsPerPage,
    order: sortDir,
    sortByKey: sortKey
  })
  const data = useMemo<PaginatedList<ActionItem> | undefined>(
    () => getFilteredData(questionCategories, filteredActions),
    [filteredActions, questionCategories]
  )

  const selectedAction =
    data && actionId
      ? [...data.list, createdAction].find((item) => item?.id === actionId)
      : undefined
  const isTableShown = !selectedAction || !isSmall

  const onRowClick = useCallback(
    (action: ActionItem) => {
      setActionId(action.id)
    },
    [setActionId]
  )

  const {isPending: actionDownloadLoading, mutate: downloadActions} =
    usePlantActionsDownload(plantId)

  const {isPending: costLeadershipLoading, mutate: downloadCostLeadership} =
    useCostLeadershipDownload(plantId)

  return (
    <PageContainer>
      <Stack
        justifyContent={'space-between'}
        pb={2}
        spacing={1}
        direction={{xs: 'column', md: 'column'}}
      >
        <FilterBar
          inverted={inverted}
          additionalActions={
            <Box sx={{display: 'flex', gap: 1, alignItems: 'stretch'}}>
              <LoadingButton
                variant="contained"
                onClick={() => downloadActions()}
                startIcon={<FileDownload />}
                loading={actionDownloadLoading}
                loadingPosition={'start'}
                sx={inverted ? mergeSxs(buttonOnBlueSx, {height: '100%'}) : {height: '100%'}}
              >
                {t('button.download')}
              </LoadingButton>
              <LoadingButton
                variant={inverted ? 'contained' : 'outlined'}
                onClick={() => downloadCostLeadership()}
                startIcon={<FileDownload />}
                loading={costLeadershipLoading}
                loadingPosition={'start'}
                sx={inverted ? buttonOnBlueSx : mergeSxs(getButtonOutlineWhite, {height: '100%'})}
              >
                <Box
                  component="span"
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    lineHeight: 1.1
                  }}
                >
                  <Typography variant="body2" component="span" color="inherit">
                    {t('button.exportCostLeadershipLine1')}
                  </Typography>
                  <Typography variant="caption" component="span" color="inherit">
                    {t('button.exportCostLeadershipLine2')}
                  </Typography>
                </Box>
              </LoadingButton>
            </Box>
          }
        />
      </Stack>
      <Grid container spacing={2}>
        {isTableShown && (
          <Grid item xs={selectedAction ? 7 : 12}>
            <ActionsContainer>
              <Box display="flex" justifyContent="flex-end" mb={1.5} px={3}>
                <ActionDialogWrapper
                  onActionCreate={(action: ActionItem) => {
                    setActionId(action.id)
                    setCreatedAction(action)
                  }}
                  showAttachments
                  showApprovers
                  opener={
                    <Button variant={'text'} startIcon={<Add />} data-test-id="add-action">
                      {t('addAction.heading')}
                    </Button>
                  }
                />
              </Box>
              <ActionsPaginatedTable
                onPageIndexChange={setPageIndex}
                pageIndex={pageIndex}
                onRowsPerPageChange={setRowsPerPage}
                rowsPerPage={rowsPerPage}
                setSortKey={setSortKey}
                sortKey={sortKey}
                onSortDirChange={setSortDir}
                sortDir={sortDir}
                onRowClick={onRowClick}
                data={data}
                isSideCardOpen={!!selectedAction}
              />
            </ActionsContainer>
          </Grid>
        )}
        {selectedAction && (
          <Grid item xs={isTableShown ? 5 : 12}>
            <ActionSideCardContainer selectedAction={selectedAction} showApprovers />
          </Grid>
        )}
      </Grid>
    </PageContainer>
  )
}
